import { memo } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    AlpacaIcon,
    ArtIcon,
    DinnerIcon,
    FireworksIcon,
    FlyIcon,
    HealthIcon,
    PureLink,
    ScreenIcon,
    SportIcon,
    SteeringWheelIcon,
    SurfIcon,
    TravelIcon,
} from '@jsmdg/yoshi';
import { type FragmentContext, type RecommendationOptions } from '../../../shared/types';
import { CategoryId } from '../../enums';
import { categoryMessages as messages } from '../../i18n';
import { Headlines } from '../Headlines';
import styles from './Categories.module.scss';

const Categories = ({ options }: { readonly options?: RecommendationOptions }): JSX.Element => {
    const intl = useIntl();
    const {
        tenantConfig: { categoryMap },
    } = useFragmentContext<FragmentContext>();

    const iconsProps = { size: 48, className: styles.categoryImage };

    const iconsMap = {
        [CategoryId.Animals]: <AlpacaIcon {...iconsProps} />,
        [CategoryId.Art]: <ArtIcon {...iconsProps} />,
        [CategoryId.Car]: <SteeringWheelIcon {...iconsProps} />,
        [CategoryId.Dinner]: <DinnerIcon {...iconsProps} />,
        [CategoryId.Fireworks]: <FireworksIcon {...iconsProps} />,
        [CategoryId.Fly]: <FlyIcon {...iconsProps} />,
        [CategoryId.Health]: <HealthIcon {...iconsProps} />,
        [CategoryId.Sport]: <SportIcon {...iconsProps} />,
        [CategoryId.Surf]: <SurfIcon {...iconsProps} />,
        [CategoryId.Travel]: <TravelIcon {...iconsProps} />,
        [CategoryId.Tv]: <ScreenIcon {...iconsProps} />,
    };

    return (
        <>
            <Headlines headline={options?.headline} subHeadline={options?.subHeadline} />
            <div className={styles.categoriesWrapper}>
                <div
                    className={classNames(
                        styles.categories,
                        'd-flex flex-sm-wrap justify-content-sm-center position-relative mt-2x overflow-auto pb-2x',
                    )}
                >
                    {categoryMap.map(categoryLink => {
                        const { messageId, url } = categoryLink;

                        const IconComponent = iconsMap[messageId as CategoryId];

                        return (
                            <PureLink
                                href={url}
                                key={messageId}
                                className={classNames(
                                    styles.category,
                                    'd-flex flex-column align-items-center pt-3x px-1x mb-4x mb-lg-3x text-decoration-none',
                                )}
                                internal
                            >
                                {IconComponent}
                                <h6 className="text-center my-1x">
                                    {intl.formatMessage(
                                        messages[messageId as keyof typeof messages], // @TODO: This needs to be made tenant aware
                                    )}
                                </h6>
                            </PureLink>
                        );
                    })}
                </div>
                <div className={classNames(styles.background, 'position-relative')} />
            </div>
        </>
    );
};

const memoCategories = memo(Categories);

export { memoCategories as Categories };
